import {
  CUSTOM_FIELD_TABS,
  CUSTOM_FIELD_TYPES,
  emptyCustomFieldsByTab
} from '@/vacancy_request/components/template-request-form/custom-fields/constants';

const consultantFieldMap = {
  client: 'area',
  subclient: 'subarea'
};

const integerTemplateFieldKeys = new Set([
  'business_group_type',
  'applicantName',
  'applicantEmail',
  'area',
  'subarea',
  'client',
  'subclient',
  'country',
  'zona',
  'locality',
  'name',
  'c_area',
  'c_subarea',
  'c_modality',
  'search_reason',
  'count_post',
  'c_type_jobs',
  'apto_discapacitados',
  'experience',
  'c_hierarchy',
  'selectedLanguages',
  'hasPeopleInCharge',
  'travel_availability',
  'requirements',
  'benefits',
  'attachedFile'
]);

const booleanTemplateFieldKeys = new Set(['areaApprovalRequired', 'subareaApprovalRequired']);

const normalizeFieldKeyForTemplateForm = (fieldKey) => consultantFieldMap[fieldKey] || fieldKey;

const normalizeTemplateFieldRequiredValue = (fieldKey, value) => {
  const normalizedFieldKey = normalizeFieldKeyForTemplateForm(fieldKey);

  if (booleanTemplateFieldKeys.has(normalizedFieldKey)) {
    return typeof value === 'string' ? value === 'true' : Boolean(value);
  }

  if (integerTemplateFieldKeys.has(normalizedFieldKey)) {
    const parsedValue = Number.parseInt(value, 10);
    return Number.isNaN(parsedValue) ? value : parsedValue;
  }

  return value;
};

const normalizeTemplateOptionRequiredValue = (value, fallback = '0') => {
  if (typeof value === 'boolean') return value ? '1' : '2';

  if (typeof value === 'number') return value.toString();

  if (typeof value === 'string') {
    const normalizedValue = value.trim().toLowerCase();
    if (normalizedValue === 'true') return '1';
    if (normalizedValue === 'false') return '2';
    if (normalizedValue === '0' || normalizedValue === '1' || normalizedValue === '2') return normalizedValue;
  }

  return fallback;
};

/**
 * Campos custom: forma del api (un array plano con `tab` y `order`) ↔ forma interna del formulario
 * (un array por pestaña). Es la mitad del límite entre el modelo y el form — la otra mitad, para el
 * formulario de solicitud, está en custom-fields/mapper.js.
 */
function formatCustomFieldsToForm(customFields) {
  const customFieldsByTab = emptyCustomFieldsByTab();
  if (!Array.isArray(customFields)) return customFieldsByTab;

  customFields
    .filter((field) => field && customFieldsByTab[field.tab])
    .slice()
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .forEach((field) => {
      customFieldsByTab[field.tab].push({
        _id: field._id ? String(field._id) : null,
        name: field.name ?? '',
        label: field.label ?? '',
        type: field.type ?? CUSTOM_FIELD_TYPES.OPEN,
        options: Array.isArray(field.options) ? field.options.map(String) : [],
        // El tri-estado se maneja como string en el formulario, igual que los campos fijos.
        required: normalizeTemplateOptionRequiredValue(field.required, '2')
      });
    });

  return customFieldsByTab;
}

function serializeCustomFields(customFieldsByTab) {
  if (!customFieldsByTab || typeof customFieldsByTab !== 'object') return [];

  return Object.values(CUSTOM_FIELD_TABS).flatMap((tab) => {
    const tabFields = Array.isArray(customFieldsByTab[tab]) ? customFieldsByTab[tab] : [];

    return tabFields.map((field, index) => {
      // `tab` sale de la key del array y `order` del índice: no se guardan en el item para no tener
      // dos fuentes de verdad con el orden real del array.
      const serializedField = {
        tab,
        order: index,
        name: (field.name ?? '').trim(),
        label: (field.label ?? '').trim(),
        type: field.type,
        required: Number.parseInt(field.required, 10)
      };

      // Sin `_id` el api genera uno nuevo; con `_id` conserva la identidad del campo entre ediciones.
      if (field._id) serializedField._id = field._id;
      if (field.type === CUSTOM_FIELD_TYPES.CLOSED) {
        serializedField.options = Array.isArray(field.options) ? field.options : [];
      }

      return serializedField;
    });
  });
}

export function formatDataToBulkOnForm(templateData) {
  const { _id: templateId, fields: templateFields, templateName } = templateData;
  if (!Array.isArray(templateFields)) {
    throw new Error('Template data must be an array');
  }

  const dataResult = {};
  dataResult.id = templateId;
  dataResult.template_name = templateName;
  dataResult.customFields = formatCustomFieldsToForm(templateData.customFields);

  const normalizeLegacyLanguageRequiredLevel = (selectedLanguages) => {
    if (!Array.isArray(selectedLanguages) || selectedLanguages.length === 0) {
      return '0';
    }

    const levels = selectedLanguages.map((lang) => `${lang?.required ?? ''}`.trim());

    if (levels.includes('1')) return '1';
    if (levels.includes('2')) return '2';
    return '0';
  };

  templateFields.forEach((field) => {
    const normalizedFieldKey = normalizeFieldKeyForTemplateForm(field.key);

    if (field.key === 'localityData') {
      if (!Array.isArray(field.required)) {
        console.warn('Locality data is not an array');
        return;
      }

      const localityData = field.required;
      const formattedValues = localityData.reduce((acc, curr) => {
        acc[curr.key] = normalizeTemplateOptionRequiredValue(curr.required);
        return acc;
      }, {});

      Object.assign(dataResult, formattedValues);
      return;
    }

    if (field.key === 'selectedLanguages') {
      if (Array.isArray(field.required)) {
        dataResult[normalizedFieldKey] = normalizeLegacyLanguageRequiredLevel(field.required);
        return;
      }

      if (typeof field.required === 'number') {
        dataResult[normalizedFieldKey] = field.required.toString();
        return;
      }

      dataResult[normalizedFieldKey] = field.required ?? '0';
      return;
    }

    if (field.key === 'business_group_type' && field.default !== null && field.default !== undefined) {
      dataResult.templatePortalData = field.default;
      if (field.default_name) {
        dataResult.templatePortalDataName = field.default_name;
      }
    }

    if (field.key === 'area' && field.default !== null && field.default !== undefined) {
      dataResult.templateAreaId = field.default;
      dataResult.templateClientId = field.default;
    }

    if (field.key === 'client' && field.default !== null && field.default !== undefined) {
      dataResult.templateClientId = field.default;
    }

    if (normalizedFieldKey === 'subarea' && field.default !== null && field.default !== undefined) {
      dataResult.templateSubareaId = field.default;
    }

    if (booleanTemplateFieldKeys.has(normalizedFieldKey)) {
      dataResult[normalizedFieldKey] = field.required === 1 || field.required === true;
      return;
    }

    if (typeof field.required === 'number') {
      dataResult[normalizedFieldKey] = field.required.toString();
      return;
    }

    if (typeof field.required === 'boolean') {
      dataResult[normalizedFieldKey] = normalizeTemplateOptionRequiredValue(field.required);
      return;
    }

    dataResult[normalizedFieldKey] = field.required;
  });

  return dataResult;
}

export function serializeTemplateFormPayload(formValues) {
  const payload = {
    template_name: formValues.template_name
  };

  if (formValues.id) {
    payload.id = formValues.id;
  }

  const appendField = (fieldKey, requiredValue) => {
    payload[fieldKey] = normalizeTemplateFieldRequiredValue(fieldKey, requiredValue);
  };

  const derivedBusinessGroupType = formValues.templatePortalData ? 1 : 0;
  appendField('business_group_type', derivedBusinessGroupType);
  if (formValues.templatePortalData) {
    payload.templatePortalData = formValues.templatePortalData;
    if (formValues.templatePortalDataName) {
      payload.templatePortalDataName = formValues.templatePortalDataName;
    }
  }
  appendField('applicantName', formValues.applicantName);
  appendField('applicantEmail', formValues.applicantEmail);
  appendField('area', formValues.area);
  appendField('subarea', formValues.subarea);
  appendField('areaApprovalRequired', formValues.areaApprovalRequired);
  appendField('subareaApprovalRequired', formValues.subareaApprovalRequired);
  appendField('country', formValues.country);
  appendField('zona', formValues.zona);
  appendField('locality', formValues.locality);
  appendField('name', formValues.name);
  appendField('c_area', formValues.c_area);
  appendField('c_subarea', formValues.c_subarea);
  appendField('c_modality', formValues.c_modality);
  appendField('search_reason', formValues.search_reason);
  appendField('count_post', formValues.count_post);
  appendField('c_type_jobs', formValues.c_type_jobs);
  appendField('apto_discapacitados', formValues.apto_discapacitados);
  appendField('experience', formValues.experience);
  appendField('c_hierarchy', formValues.c_hierarchy);
  appendField('selectedLanguages', formValues.selectedLanguages);
  appendField('hasPeopleInCharge', formValues.hasPeopleInCharge);
  appendField('travel_availability', formValues.travel_availability);
  appendField('requirements', formValues.requirements);
  appendField('benefits', formValues.benefits);
  appendField('attachedFile', formValues.attachedFile);

  if (formValues.templateAreaId) {
    payload.templateAreaId = formValues.templateAreaId;
  }

  if (formValues.templateClientId) {
    payload.templateClientId = formValues.templateClientId;
  }

  if (formValues.templateSubareaId) {
    payload.templateSubareaId = formValues.templateSubareaId;
  }

  // Única key anidada de un payload que por lo demás es plano: no pasa por `appendField`.
  payload.customFields = serializeCustomFields(formValues.customFields);

  return payload;
}
